"use client"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import React, {
  ComponentPropsWithoutRef,
  ElementRef,
  createContext,
  forwardRef,
  useContext,
  useMemo,
} from "react"
import { Check, CircleFilled } from "../../icons"
import { disabledVariants } from "../../styles"
import { VariantProps, classNames, tv } from "../../utils"
import { Media } from "../Media"

export type RadioGroupProps = Pick<
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
  | "children"
  | "className"
  | "defaultValue"
  | "disabled"
  | "value"
  | "onValueChange"
  | "name"
  | "loop"
> &
  VariantProps<typeof radioGroupItemVariants>

const RadioGroupContext = createContext<{
  variant: NonNullable<VariantProps<typeof radioGroupItemVariants>["variant"]>
}>({
  variant: "default",
})

const RadioGroupBase = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(function RadioGroupBase({ className, variant = "default", ...props }, ref) {
  const value = useMemo(() => ({ variant }), [variant])

  return (
    <RadioGroupContext.Provider value={value}>
      <RadioGroupPrimitive.Root
        className={classNames("grid gap-4", className)}
        {...props}
        ref={ref}
      />
    </RadioGroupContext.Provider>
  )
})

type RadioGroupItemPropsBase = Pick<
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
  "className" | "disabled" | "id" | "checked"
>

type RadioGroupItemPropsWithValue = RadioGroupItemPropsBase & {
  value: string
}

type RadioGroupItemPropsWithChecked = RadioGroupItemPropsBase & {
  checked: boolean
}

export type RadioGroupItemProps =
  | RadioGroupItemPropsWithValue
  | RadioGroupItemPropsWithChecked

const radioGroupItemVariants = tv({
  base: "rounded-full outline-hidden transition-colors",
  variants: {
    variant: {
      default:
        "size-6 min-h-[24px] min-w-[24px] border-2 data-[state=unchecked]:border-border-3 data-[state=unchecked]:focus:border-border-3",
      checkmark:
        "size-6 min-h-[24px] min-w-[24px] border border-border-1 bg-bg-additional-2 data-[state=checked]:bg-blue-3 md:size-7",
    },
  },
})

export const RadioGroupItem = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(function RadioGroupItem({ className, disabled, ...props }, ref) {
  const { variant } = useContext(RadioGroupContext)

  return (
    <RadioGroupPrimitive.Item
      className={classNames(
        radioGroupItemVariants({ variant }),
        disabledVariants({ disabled }),
        className,
      )}
      disabled={disabled}
      ref={ref}
      value={"value" in props ? props.value : ""}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center animate-in fade-in">
        {variant === "default" ? (
          <CircleFilled size={12} />
        ) : (
          <>
            <Media lessThan="md">
              <Check fill="white" size={20} />
            </Media>
            <Media greaterThanOrEqual="md">
              <Check fill="white" size={24} />
            </Media>
          </>
        )}
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})

/**
 * Displays a list of radio toggles.
 */
export const RadioGroup = Object.assign(RadioGroupBase, {
  Item: RadioGroupItem,
})
